<template>
  <div class="kb-page">
    <Header />
    <div class="kb-container">
    <!-- 左侧边栏：会话列表 -->
    <aside class="sidebar">
      <div class="sidebar-header">
        <h2>知识库助手</h2>
        <el-button type="primary" size="small" icon="el-icon-plus" @click="createSession">新建对话</el-button>
      </div>

      <div class="session-list">
        <div
          v-for="session in sessions"
          :key="session.sessionId"
          :class="['session-item', { active: currentSessionId === session.sessionId }]"
          @click="selectSession(session)"
        >
          <span class="session-title">{{ session.title }}</span>
          <el-button
            type="text"
            class="delete-btn"
            icon="el-icon-delete"
            @click.stop="deleteSession(session.sessionId)"
          ></el-button>
        </div>

        <div v-if="sessions.length === 0" class="empty-tip">
          暂无会话记录
        </div>
      </div>
    </aside>

    <!-- 右侧主聊天区 -->
    <main class="chat-area">
      <!-- 消息列表 -->
      <div class="message-list" ref="messageList">
        <div v-if="messages.length === 0" class="welcome-tip">
          <div class="welcome-icon">🤖</div>
          <h3>欢迎使用知识库助手</h3>
          <p>基于知识库内容，我可以回答你的问题</p>
          <div class="quick-questions">
            <el-tag
              v-for="q in quickQuestions"
              :key="q"
              class="quick-tag"
              @click="askQuestion(q)"
            >{{ q }}</el-tag>
          </div>
        </div>

        <div
          v-for="(msg, index) in messages"
          :key="index"
          :class="['message', msg.role]"
        >
          <div class="avatar">{{ msg.role === 'user' ? '👤' : '🤖' }}</div>
          <div class="content">
            <div class="text">
              <span v-html="formatMessage(msg.content)"></span>
              <!-- 流式加载中显示光标 -->
              <span v-if="msg.loading" class="streaming-cursor"></span>
            </div>

            <!-- 引用来源 -->
            <div v-if="msg.references && msg.references.length > 0" class="references">
              <div class="ref-title">📚 参考来源：</div>
              <div
                v-for="(ref, refIdx) in msg.references"
                :key="refIdx"
                class="ref-item"
              >
                <span class="ref-name">{{ ref.title }}</span>
                <span class="ref-category">{{ ref.category }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- 加载中 -->
        <div v-if="loading" class="message assistant loading">
          <div class="avatar">🤖</div>
          <div class="content">
            <div class="text">
              <span class="typing-dot">●</span>
              <span class="typing-dot">●</span>
              <span class="typing-dot">●</span>
              AI思考中...
            </div>
          </div>
        </div>
      </div>

      <!-- 输入区 -->
      <div class="input-area">
        <div class="mode-toggle">
          <el-switch
            v-model="streamMode"
            active-text="流式"
            inactive-text="非流式"
            disabled
            title="流式模式暂时不可用，请使用非流式模式"
          >
          </el-switch>
        </div>
        <textarea
          v-model="inputMessage"
          class="message-input"
          placeholder="输入你的问题... (Enter发送, Shift+Enter换行)"
          rows="2"
          @keydown.enter.exact.prevent="sendMessage"
        ></textarea>
        <div class="input-actions">
          <span class="shortcut">Enter 发送 / Shift+Enter 换行</span>
          <el-button
            type="primary"
            :loading="loading && !streamMode"
            :disabled="!inputMessage.trim() || (loading && !streamMode)"
            @click="sendMessage"
          >
            {{ loading && !streamMode ? '思考中...' : '发送' }}
          </el-button>
      </div>
      </div>
    </main>
  </div>
</div>
</template>

<script>
import axios from '../axios'
import Header from '@/components/Header.vue'

export default {
  name: 'KnowledgeBase',

  components: { Header },

  data() {
    return {
      // 会话相关
      sessions: [],
      currentSessionId: null,

      // 消息相关
      messages: [],
      inputMessage: '',
      loading: false,

      // 快捷问题
      quickQuestions: [
        'Spring AI 如何集成？',
        'MyBatis-Plus 分页怎么用？',
        'JWT 认证流程是什么？'
      ],

      // 流式输出开关
      streamMode: true
    }
  },

  mounted() {
    this.loadSessions()
  },

  methods: {
    // 加载会话列表
    async loadSessions() {
      try {
        const res = await axios.get('/ai/kb/sessions')
        if (res.data.code === 200) {
          this.sessions = res.data.data || []
        }
      } catch (err) {
        console.error('加载会话失败', err)
      }
    },

    // 创建新会话
    async createSession() {
      try {
        const res = await axios.post('/ai/kb/session')
        if (res.data.code === 200) {
          this.sessions.unshift(res.data.data)
          this.selectSession(res.data.data)
        }
      } catch (err) {
        console.error('创建会话失败', err)
      }
    },

    // 选择会话
    async selectSession(session) {
      this.currentSessionId = session.sessionId
      try {
        const res = await axios.get(`/ai/kb/history/${session.sessionId}`)
        if (res.data.code === 200) {
          this.messages = (res.data.data || []).map(msg => {
            let references = []
            if (msg.referencesContent) {
              try {
                references = JSON.parse(msg.referencesContent)
              } catch (e) {
                references = []
              }
            }
            return { ...msg, references }
          })
          this.scrollToBottom()
        }
      } catch (err) {
        console.error('加载历史消息失败', err)
      }
    },

    // 删除会话
    async deleteSession(sessionId) {
      try {
        await this.$confirm('确定删除该会话吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })

        const res = await axios.delete(`/ai/kb/session/${sessionId}`)
        if (res.data.code === 200) {
          this.sessions = this.sessions.filter(s => s.sessionId !== sessionId)
          if (this.currentSessionId === sessionId) {
            this.currentSessionId = null
            this.messages = []
          }
          this.$message.success('删除成功')
        }
      } catch (err) {
        if (err !== 'cancel') {
          console.error('删除会话失败', err)
        }
      }
    },

    // 发送消息
    async sendMessage() {
      if (!this.inputMessage.trim()) return

      const message = this.inputMessage.trim()
      this.inputMessage = ''

      // 添加用户消息
      this.messages.push({
        role: 'user',
        content: message,
        references: []
      })

      this.scrollToBottom()

      // 根据模式选择发送方式
      if (this.streamMode) {
        await this.sendMessageStream(message)
      } else {
        await this.sendMessageNormal(message)
      }
    },

    // 非流式发送
    async sendMessageNormal(message) {
      this.loading = true
      try {
        const res = await axios.post('/ai/kb/chat', {
          sessionId: this.currentSessionId,
          message: message
        })

        if (res.data.code === 200) {
          const data = res.data.data

          // 更新会话ID（如果是新会话）
          if (!this.currentSessionId) {
            this.currentSessionId = data.sessionId
          }

          // 添加AI回复
          this.messages.push({
            role: 'assistant',
            content: data.answer,
            references: data.references || []
          })

          // 更新会话列表
          this.loadSessions()
        } else {
          this.$message.error(res.data.msg || '请求失败')
        }
      } catch (err) {
        console.error('发送消息失败', err)
        this.$message.error('发送失败，请重试')
      } finally {
        this.loading = false
        this.scrollToBottom()
      }
    },

    // 流式发送
    async sendMessageStream(message) {
      // 添加一个空的AI消息占位
      const aiMsgIndex = this.messages.length
      this.messages.push({
        role: 'assistant',
        content: '',
        references: [],
        loading: true
      })
      this.scrollToBottom()

      try {
        const response = await fetch('/api/ai/kb/chat/stream', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': localStorage.getItem('token') || ''
          },
          body: JSON.stringify({
            sessionId: this.currentSessionId,
            message: message
          })
        })

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`)
        }

        // 读取流
        const reader = response.body.getReader()
        const decoder = new TextDecoder()
        let buffer = ''

        while (true) {
          const { done, value } = await reader.read()
          if (done) break

          buffer += decoder.decode(value, { stream: true })

          // 处理SSE数据
          const lines = buffer.split('\n')
          buffer = lines.pop() // 保留未完成的行

          for (const line of lines) {
            if (line.startsWith('data:')) {
              const data = line.slice(5).trim()
              if (data && data !== '[DONE]') {
                // 累积到当前消息
                this.messages[aiMsgIndex].content += data
                this.scrollToBottom()
              }
            }
          }
        }

        // 完成后刷新会话列表
        this.loadSessions()
        this.messages[aiMsgIndex].loading = false

      } catch (err) {
        console.error('流式发送失败', err)
        this.messages[aiMsgIndex].content = '流式输出失败，请切换到非流式模式重试'
        this.messages[aiMsgIndex].loading = false
        this.$message.error('流式输出失败: ' + err.message)
      }
    },

    // 快捷提问
    askQuestion(question) {
      this.inputMessage = question
      this.sendMessage()
    },

    // 格式化消息（简单的markdown处理）
    formatMessage(text) {
      if (!text) return ''
      return text
        .replace(/\n/g, '<br>')
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.*?)\*/g, '<em>$1</em>')
        .replace(/`(.*?)`/g, '<code>$1</code>')
    },

    // 滚动到底部
    scrollToBottom() {
      this.$nextTick(() => {
        const el = this.$refs.messageList
        if (el) {
          el.scrollTop = el.scrollHeight
        }
      })
    }
  }
}
</script>

<style scoped>
.kb-page {
  min-height: 100vh;
  background: #f5f5fb;
}

.kb-container {
  display: flex;
  height: calc(100vh - 56px);
  background: #f5f5fb;
}

/* 侧边栏 */
.sidebar {
  width: 260px;
  background: #fff;
  border-right: 1px solid #e8e8e8;
  display: flex;
  flex-direction: column;
}

.sidebar-header {
  padding: 16px;
  border-bottom: 1px solid #e8e8e8;
}

.sidebar-header h2 {
  margin: 0 0 12px 0;
  font-size: 16px;
  color: #333;
}

.session-list {
  flex: 1;
  overflow-y: auto;
  padding: 8px;
}

.session-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  margin: 4px 0;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.2s;
}

.session-item:hover {
  background: #f0f7ff;
}

.session-item.active {
  background: #e6f0ff;
  color: #58a8e0;
}

.session-title {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
}

.delete-btn {
  opacity: 0;
  color: #999;
}

.session-item:hover .delete-btn {
  opacity: 1;
}

.delete-btn:hover {
  color: #f56c6c !important;
}

.empty-tip {
  text-align: center;
  color: #999;
  padding: 40px 0;
  font-size: 14px;
}

/* 聊天区 */
.chat-area {
  flex: 1;
  display: flex;
  flex-direction: column;
  max-width: 900px;
  margin: 0 auto;
  width: 100%;
}

.message-list {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
}

.welcome-tip {
  text-align: center;
  padding: 60px 20px;
}

.welcome-icon {
  font-size: 48px;
  margin-bottom: 16px;
}

.welcome-tip h3 {
  margin: 0 0 8px 0;
  color: #333;
}

.welcome-tip p {
  color: #999;
  margin-bottom: 20px;
}

.quick-questions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: center;
}

.quick-tag {
  cursor: pointer;
  //padding: 8px px;
}

.quick-tag:hover {
  background: #58a8e0;
  color: #fff;
}

/* 消息 */
.message {
  display: flex;
  margin-bottom: 20px;
}

.message.user {
  flex-direction: row-reverse;
}

.avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  flex-shrink: 0;
}

.message.user .avatar {
  background: #58a8e0;
  margin-left: 12px;
}

.message.assistant .avatar {
  background: #f0f0f0;
  margin-right: 12px;
}

.content {
  max-width: 75%;
}

.text {
  padding: 12px 16px;
  border-radius: 12px;
  line-height: 1.6;
  font-size: 14px;
}

.message.user .text {
  background: #58a8e0;
  color: #fff;
  border-bottom-right-radius: 4px;
}

.message.assistant .text {
  background: #fff;
  color: #333;
  border-bottom-left-radius: 4px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.08);
}

.typing-dot {
  animation: blink 1s infinite;
}

@keyframes blink {
  0%, 100% { opacity: 0.3; }
  50% { opacity: 1; }
}

/* 流式加载中光标 */
.streaming-cursor {
  display: inline-block;
  width: 2px;
  height: 1em;
  background: #58a8e0;
  margin-left: 2px;
  animation: cursor-blink 0.8s infinite;
  vertical-align: middle;
}

@keyframes cursor-blink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
}

/* 引用来源 */
.references {
  margin-top: 8px;
  padding: 8px 12px;
  background: #f8f8f8;
  border-radius: 6px;
  font-size: 12px;
}

.ref-title {
  color: #666;
  margin-bottom: 6px;
}

.ref-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
}

.ref-name {
  color: #58a8e0;
}

.ref-category {
  color: #999;
  font-size: 11px;
  background: #eee;
  padding: 2px 6px;
  border-radius: 3px;
}

/* 输入区 */
.input-area {
  padding: 16px 20px;
  background: #fff;
  border-top: 1px solid #e8e8e8;
}

.mode-toggle {
  margin-bottom: 10px;
}

.mode-toggle .el-switch {
  font-size: 12px;
}

.message-input {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  line-height: 1.5;
  resize: none;
  outline: none;
  transition: border-color 0.2s;
  box-sizing: border-box;
}

.message-input:focus {
  border-color: #58a8e0;
}

.input-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 10px;
}

.shortcut {
  color: #999;
  font-size: 12px;
}
</style>
